<template>
  <ArticlesHeader
    :loading="loading"
    :title="header"
    :subtitle="subheader"
    :country="country"
    :city="city"
    events-identifier="city-articles-info"
  />

  <NavigationBar
    :nav="navigation"
    :loading="dataLoading"
    :scroll-offset="20"
    sticky
    @section-selected="handleSectionSelection"
  />

  <ArticlesList>
    <JournalContentListWrapper
      :subject-geo-id="cityId"
      :subject-geo-name="cityName"
      :general-page-data-loading="loading"
    >
      <JournalContentList
        :page-size="articlesListPageSize"
        :city-slug="slug"
        :subject-geo-id="cityId"
        :subject-geo-name="cityName"
        :general-page-data-loading="loading"
      />
    </JournalContentListWrapper>
  </ArticlesList>

  <SightsBlock v-if="showSightsBlock">
    <SightsListWrapper
      :subject-geo-id="cityId"
      :subject-geo-name="cityName"
      :general-page-data-loading="loading"
    >
      <SightsList
        :all-sights-link="allSightsLink"
        :subject-geo-id="cityId"
        :subject-geo-name="cityName"
        :general-page-data-loading="loading"
      />
    </SightsListWrapper>
  </SightsBlock>

  <ExperiencesList
    :subject-geo-id="cityId"
    :subject-geo-name="cityName"
    :in-obj-phrase="inObjPhrase"
    :general-page-data-loading="loading"
  >
    <ExperienceListing
      :skeletons-count="3"
      more-button
      :more-button-text="t('seeAll')"
      :more-button-link="allExperiencesLink"
      :general-page-data-loading="loading"
      analytic-events-prefix="city-articles-info"
      @load-more="showAllExperiences"
    />
  </ExperiencesList>

  <SubscriptionBlock
    api-url=""
    :analytics-api-url="VITE_RUNTIME_SOGU_SERVICE_URL"
  />

  <SeoBlock v-if="seoText">
    <Seo />
  </SeoBlock>
</template>

<script lang="ts" setup>
  import { useI18n } from '@experience/i18n';
  import { SubscriptionBlock } from '@experience/subscription-block-package';
  import { useHead } from '@unhead/vue';
  import { useRuntimeConfig } from 'virtual:runtime-config';
  import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue';
  import { useRoute } from 'vue-router';

  import { trackEvent } from '@/services/analytics';
  import { MF_NAME } from '@/app/const';
  import ExperienceListing from '@/features/ExperienceListing.vue';
  import { NavigationBar } from '@/features/navigationBar';
  import Seo from '@/features/Seo.vue';
  import { useExperienceEntity } from '@/entities/experience';
  import { useJournalContent } from '@/entities/journalContent';
  import { useSeoEntity } from '@/entities/seo';
  import { useSightEntity } from '@/entities/sight';
  import type { CityArticlesInfoSectionClickParams } from '@/shared/api/sogu';
  import { getCanonicalPrefix } from '@/shared/utils/canonicalPrefix';
  import { tryAwait } from '@/shared/utils/tryAwait';

  import { useCityArticlesEntity } from './entities/cityArticles';
  import { ArticlesHeader } from './features/articlesHeader';

  import { ExperiencesList } from './features/experiencesList';
  import { JournalContentList, JournalContentListWrapper } from './features/journalContentList';
  import { SightsList, SightsListWrapper } from './features/sightsList';
  import { ru } from './i18n/ru';
  import ArticlesList from './widgets/ArticlesList.vue';
  import SeoBlock from './widgets/SeoBlock.vue';
  import SightsBlock from './widgets/sightsBlock/ui/SightsBlock.vue';

  const { t } = useI18n({ inheritLocale: true, messages: { ru } });

  const { VITE_RUNTIME_SOGU_SERVICE_URL } = useRuntimeConfig()[MF_NAME];
  const route = useRoute();

  const seoEntity = useSeoEntity();
  const sightsEntity = useSightEntity();
  const cityArticlesEntity = useCityArticlesEntity();
  const journalContentEntity = useJournalContent();
  const experienceEntity = useExperienceEntity();

  const loading = computed(() => seoEntity.loading || cityArticlesEntity.loading);
  const showSightsBlock = computed(() => sightsEntity.loading || sightsEntity.sights.length > 0);

  const dataLoading = computed(
    () =>
      seoEntity.loading ||
      cityArticlesEntity.loading ||
      sightsEntity.loading ||
      journalContentEntity.loading ||
      experienceEntity.loading,
  );

  const articlesListPageSize = ref(15);

  const slug = computed(() => String(route.params.slug));
  const cityName = computed(() => cityArticlesEntity.city?.nameRu ?? '');
  const cityId = computed(() => cityArticlesEntity.city?.id ?? 0);
  const header = computed(() => {
    if (seoEntity.seo?.h1) {
      return seoEntity.seo.h1;
    }

    return cityArticlesEntity.city?.aboutObjPhrase
      ? t('usefulArticles', { place: cityArticlesEntity.city.aboutObjPhrase })
      : '';
  });

  const subheader = computed(() => seoEntity.seo?.h2 ?? '');
  const title = computed(() => seoEntity.seo?.title ?? '');
  const description = computed(() => seoEntity.seo?.description ?? '');
  const ogImage = computed(() => cityArticlesEntity?.city?.image.cover ?? '');
  const seoText = computed(() => seoEntity.seo?.text ?? '');

  const country = computed(() => {
    const entityCountry = cityArticlesEntity?.city?.country;

    return entityCountry ? { name: entityCountry.nameRu, slug: entityCountry.slug } : undefined;
  });

  const city = computed(() => {
    const entityCity = cityArticlesEntity?.city;

    return entityCity ? { name: entityCity.nameRu, slug: entityCity.slug } : undefined;
  });

  const inObjPhrase = computed(() => cityArticlesEntity.city?.inObjPhrase ?? '');

  const getGeneralEventParams = () =>
    ({
      subject_geo_id: cityId.value,
      subject_geo_name: cityName.value,
      subject_geo_type: 'city',
    }) as const;

  const navigation = computed(() => {
    const items = [{ dataset: 'article', title: 'Статьи' }];

    if (!sightsEntity.loading && sightsEntity.sights.length > 0) {
      items.push({ dataset: 'sight', title: 'Достопримечательности' });
    }

    items.push({ dataset: 'experience', title: 'Экскурсии' });

    return items;
  });

  useHead({
    title,
    link: [
      {
        rel: 'canonical',
        href: `${getCanonicalPrefix()}/journal/city/articles/${slug.value}/`,
      },
    ],
    meta: [
      {
        name: 'description',
        content: () => description.value,
      },
      {
        property: 'og:title',
        content: () => title.value,
      },
      {
        property: 'og:description',
        content: () => description.value,
      },
      {
        content: () => ogImage.value,
        property: 'og:image',
      },
      {
        name: 'twitter:card',
        content: () => (ogImage.value ? 'summary_large_image' : undefined),
      },
      {
        name: 'twitter:image',
        content: () => ogImage.value,
      },
    ],
  });

  const fetchData = async () => {
    const citySlugs = [slug.value];
    const seoDataPromises = [cityArticlesEntity.loadPageData(slug.value), seoEntity.loadSeo(slug.value, 'city')];

    // посылаем событие pageview сразу после загрузки базовых данных
    (async () => {
      await Promise.allSettled(seoDataPromises);
      trackEvent('city-articles-info:pageview', getGeneralEventParams());
    })();

    await Promise.allSettled([
      ...seoDataPromises,
      journalContentEntity.getArticles({ pageSize: articlesListPageSize.value, slug: slug.value, slugType: 'city' }),
      sightsEntity.getSights({ pageSize: 5, citySlugs }),
      experienceEntity.load({
        citySlugs,
        limit: 6,
        offset: 0,
        priceFormat: 'travelers',
        extendedMovementTypes: true,
        photosCount: 1,
      }),
    ]);
  };

  onBeforeMount(() => fetchData());
  onMounted(() => trackEvent('city-articles-info:pageview_loading', { slug: slug.value }));

  onBeforeUnmount(() => {
    journalContentEntity.reset();
    sightsEntity.reset();
    experienceEntity.reset();
    seoEntity.reset();
  });

  const allSightsLink = computed(() => `/experience/${slug.value}/articles/sights/`);

  const allExperiencesLink = computed(() => `/experience/${slug.value}/`);

  const showAllExperiences = async () => {
    await tryAwait(() =>
      trackEvent('city-articles-info:section:show-all.click', {
        ...getGeneralEventParams(),
        section_type: 'experience',
      }),
    );
    // TODO: заменить на routerProxy.push после окончательного перехода на МФ листинга.
    window.location.href = allExperiencesLink.value;
  };

  const handleSectionSelection = (dataset: string) => {
    trackEvent('city-articles-info:section.click', {
      section_type: dataset as CityArticlesInfoSectionClickParams['params']['section_type'],
      ...getGeneralEventParams(),
    });
  };
</script>
